/**
 * Screenshot rules for the bug-report widget. They mirror what the API accepts (routes/issues.ts): a raster
 * image sent as a data URL, and only by a signed-in reporter.
 */

export const ACCEPTED_SCREENSHOT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/avif',
  'image/heic',
  'image/heif',
] as const;

export const ACCEPTED_SCREENSHOT_LABEL = 'PNG, JPEG, GIF, WebP, BMP, AVIF or HEIC';

/** Value for the file input's `accept` attribute. */
export const SCREENSHOT_ACCEPT_ATTR = ACCEPTED_SCREENSHOT_TYPES.join(',');

export function isAcceptedScreenshotType(type: string): boolean {
  return (ACCEPTED_SCREENSHOT_TYPES as readonly string[]).includes(type.toLowerCase());
}

/** Only inline raster images are ever rendered as a screenshot (never a remote URL the browser would fetch). */
export function isSafeScreenshotSrc(src: string | null | undefined): src is string {
  return typeof src === 'string' && /^data:image\/(png|jpe?g|gif|webp|bmp|avif|heic|heif);base64,/i.test(src);
}

/** A plain-language message for a failed submission, from the API error envelope or a network failure. */
export function describeSubmitError(error: { code?: string; message?: string } | undefined | null): string {
  switch (error?.code) {
    case 'RATE_LIMITED':
      return 'Too many reports in a short time. Please wait a few minutes and try again.';
    case 'VALIDATION_ERROR':
      return error.message || 'Some details are not valid. Please check the report and try again.';
    case 'DB_ERROR':
      return 'We could not save your report right now. Please try again in a moment.';
    default:
      if (error?.code === 'HTTP_503') {
        return 'Reports are temporarily limited. Please try again later.';
      }
      return error?.message || 'Failed to submit';
  }
}
